import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, Music, ArrowRight, Home } from "lucide-react";
import { motion } from "framer-motion";

export default function SubmitSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get submission details from navigation state
  const { trackTitle, playlistCount, totalCost } = location.state || {};

  useEffect(() => {
    document.title = "Submission Successful - Soundope";
  }, []);

  return (
    <div className="min-h-screen px-4 py-12 pb-32 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <div className="neuro-base rounded-3xl p-8 text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-green-400" />
          </motion.div>

          {/* Main Message */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-[#d0d0d0] mb-3"
          >
            Successfully Submitted!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-[#a0a0a0] mb-6"
          >
            {trackTitle ? (
              <>Your track "<span className="text-[#d0d0d0] font-medium">{trackTitle}</span>" has been pitched to {playlistCount} playlist{playlistCount > 1 ? 's' : ''}.</>
            ) : (
              <>Your track has been pitched to the curators.</>
            )}
          </motion.p>

          {/* Timeline Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="neuro-pressed rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-purple-400" />
              <span className="text-xl font-semibold text-[#d0d0d0]">What's Next?</span>
            </div>
            <p className="text-[#909090] leading-relaxed">
              Curators will review your submission and you'll hear back within the next{" "}
              <span className="text-purple-400 font-bold text-lg">72 hours</span>!
            </p>
            <p className="text-sm text-[#707070] mt-3">
              You'll receive a notification when a curator responds to your pitch.
            </p>
          </motion.div>

          {/* Stats if available */}
          {totalCost && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center gap-6 mb-8 text-sm text-[#808080]"
            >
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4" />
                <span>{playlistCount} playlist{playlistCount > 1 ? 's' : ''}</span>
              </div>
              <div className="w-px h-4 bg-[#3a3a3a]" />
              <div>
                <span>{totalCost} credit{totalCost > 1 ? 's' : ''} used</span>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Button
              onClick={() => navigate("/Submit")}
              variant="outline"
              className="flex-1 neuro-flat border-0 text-[#a0a0a0] hover:text-[#d0d0d0]"
            >
              <Music className="w-4 h-4 mr-2" />
              Submit Another Track
            </Button>
            <Button
              onClick={() => navigate("/Discover")}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            >
              Discover Music
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          {/* Secondary Link */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={() => navigate("/")}
            className="mt-6 text-sm text-[#606060] hover:text-[#909090] transition-colors flex items-center gap-1 mx-auto"
          >
            <Home className="w-3 h-3" />
            Back to Home
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
